import { adminTheme, agronodTheme } from "@agronod/mui-components";
import type { Theme } from "@mui/material/styles";

/**
 * The design-system themes ship palette and component overrides but no
 * typography, so `createTheme` would fall back to MUI's default Roboto. Inter
 * is injected here so the base theme, CssBaseline's body rule and every
 * component (including `fontFamily: 'inherit'` elements) render it consistently.
 */
const typography = {
  fontFamily: [
    "Inter",
    "-apple-system",
    "BlinkMacSystemFont",
    '"Segoe UI"',
    "Helvetica",
    "Arial",
    "sans-serif",
  ].join(","),
};

/**
 * Dialog content padding on a phone, for every dialog in the app. The
 * library's `AgronodDialog` pads its content 40 px on each side; on a phone
 * the dialog is a full-width sheet and 80 px left the text in a narrow column
 * (Tove, 2026-10-17). The library sets that padding through `sx`, which a
 * plain content override loses to, so the rule is scoped through the dialog
 * paper: two classes beat the one `sx` emits. A dialog's own `dialogContentSx`
 * therefore only applies from `sm` up.
 *
 * It lives on `MuiDialogContent`, not on `MuiDialog.paper`: the library's
 * `ThemeProvider` deep-merges these options into its base theme, and a
 * function there would replace the base theme's paper rule (the 16 px corner
 * radius of every plain `Dialog`) instead of merging into it.
 */
const dialogComponents = {
  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        [theme.breakpoints.down("sm")]: {
          ".MuiDialog-paper &": {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
          },
        },
      }),
    },
  },
};

/** Theme for the lantbrukare's own views: Agronod yellow as primary. */
export const agronodThemeOptions = {
  ...agronodTheme,
  typography,
  components: { ...agronodTheme.components, ...dialogComponents },
};

/**
 * Theme for recipient-facing views (the public share page): the Admin theme's
 * burgundy scale as primary, which is the colour Agronod uses for all
 * customer/recipient surfaces. Everything else is inherited from the Agronod
 * theme, so components switch accent by theme rather than by prop.
 */
export const adminThemeOptions = {
  ...adminTheme,
  typography,
  components: { ...adminTheme.components, ...dialogComponents },
};

/**
 * Colour scale for comment boxes ("Kommentar från lantbrukaren"), in every
 * view. Deliberately the Agronod yellow, not the active theme's primary: on the
 * share page the Admin theme turns `primary` burgundy, and a burgundy comment
 * box disappears against the burgundy header and progress bar. Yellow was
 * chosen so the farmer's comments stand out for the recipient.
 */
export const commentPalette = agronodTheme.palette.primary;
